import React from 'react'
import { useNavigate } from 'react-router-dom'
import { KibChevronRightIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import ColorPinIcon from '~/components/common/icons/ColorPinIcon'
import { ControlledSubstanceChip } from '~/components/common/labels/ControlledSubstanceChip'

import styles from './RxCardHeader.module.scss'

export interface RxCardHeaderProps {
  actionNeeded?: boolean
  dateLabel?: React.ReactNode
  dateTitle: React.ReactNode
  doctorInfo?: React.ReactNode
  isControlled?: boolean
  onClick?: () => void
  originBusinessId?: string | null
  prescriptionId?: string
  subheader: React.ReactNode
  title?: React.ReactNode
  titlePinColor?: string | null
}

const RxCardHeader = ({
  actionNeeded = false,
  dateLabel,
  dateTitle,
  doctorInfo,
  isControlled = false,
  onClick,
  originBusinessId,
  prescriptionId,
  subheader,
  title,
  titlePinColor,
}: RxCardHeaderProps) => {
  const navigate = useNavigate()
  const isClickable = Boolean(onClick) || Boolean(prescriptionId)

  const onActivate = () => {
    if (onClick) {
      onClick()
      return
    }

    if (prescriptionId) {
      navigate(`/prescription/${prescriptionId}`)
    }
  }

  const header = (
    <div
      className={classNames(styles.root, {
        [styles.actionNeeded]: actionNeeded,
      })}
    >
      <div className={styles.headerRoot}>
        <span className={styles.dateTitle}>{dateTitle}</span>
        {subheader && (
          <span className={styles.headerMeta}>
            <span className={styles.dateSubheader}>{subheader}</span>
          </span>
        )}
      </div>

      {(dateLabel || doctorInfo) && (
        <div className={styles.doctorInfo}>
          {dateLabel && <span className={styles.dateLabel}>{dateLabel}</span>}
          {doctorInfo}
        </div>
      )}

      <div className={styles.titleRow}>
        <ColorPinIcon
          alignSelf="center"
          color={titlePinColor ?? null}
          variant="big"
        />
        {title && (
          <span className={styles.titleContainer}>
            {React.isValidElement(title) ? (
              title
            ) : (
              <span className={styles.title}>{title}</span>
            )}
            {isControlled && (
              <span className={styles.controlledSubstanceChip}>
                <ControlledSubstanceChip />
              </span>
            )}
          </span>
        )}
        <span className={styles.actions}>
          {isClickable && <KibChevronRightIcon className={styles.chevron} />}
          {originBusinessId && (
            <BusinessShareIcon businessIds={[originBusinessId]} />
          )}
        </span>
      </div>
    </div>
  )

  return isClickable ? (
    <button
      className={styles.clickableHeader}
      type="button"
      onClick={onActivate}
    >
      {header}
    </button>
  ) : (
    header
  )
}

export default RxCardHeader
